// =============================================================================
// src/components/agents/shared.ts — constantes, types et helpers communs au
// hub Agents (Phase 3B) : Agents / Remote Access / Diagnostics / GPU Cluster /
// Packages / Configuration. Extrait de l'ancien fichier monolithique
// ai/agents/page.tsx pour permettre l'extraction des onglets en composants
// séparés sans dupliquer ces constantes dans chaque fichier.
// =============================================================================

export const API    = process.env.NEXT_PUBLIC_API_URL ?? ''
export const ACCENT = '#4f7cff'
export const GREEN  = '#34d399'
export const AMBER  = '#f5a623'
export const RED    = '#ef4444'
export const PURPLE = '#a78bfa'
export const TEAL   = '#2dd4bf'
export const MUTED  = '#475569'
export const BORDER = 'rgba(255,255,255,0.07)'

export const STATUS_COLOR: Record<string, string> = { ONLINE: GREEN, IDLE: AMBER, OFFLINE: MUTED }
export const STATUS_LABEL: Record<string, string> = { ONLINE: 'En ligne', IDLE: 'Inactif', OFFLINE: 'Hors ligne' }
export const RISK_COLOR: Record<'low' | 'medium' | 'high', string> = { low: GREEN, medium: AMBER, high: RED }
// Phase AGIR, Niveau 2 — statut d'un ActionRequest répercuté sur son
// RecommendationAction parent (voir action-execution.ts).
export const STATUS_ACTION_COLOR: Record<string, string> = {
  blocked: AMBER, rejected: MUTED, executing: PURPLE, succeeded: GREEN, failed: RED,
}
export const STATUS_ACTION_LABEL: Record<string, string> = {
  blocked: 'BLOQUÉ', rejected: 'REFUSÉ', executing: 'EN COURS…', succeeded: 'RÉUSSI', failed: 'ÉCHEC',
}
export const PLATFORM_ICON: Record<string, string> = { linux: '🐧', windows: '🪟', darwin: '🍎', macos: '🍎' }

// Machine Identity Pipeline (Phase 1/2) — même vocabulaire GpuTier que
// modules/ai/core/model-registry.ts::tierForVram(), jamais une échelle
// inventée côté UI (voir CLAUDE.md, "UI components must not invent domain
// concepts").
export const TIER_COLOR: Record<string, string> = {
  tiny: MUTED, small: TEAL, medium: GREEN, large: PURPLE, distributed: ACCENT,
}
export const NODE_ROLE_LABEL: Record<string, string> = {
  gpu_worker: 'GPU Worker', light_client: 'Light Client',
}

// Mesh GPU (P7) — modèle Prisma SÉPARÉ de DesktopAgent ci-dessous : un agent
// bureau appairé peut EN PLUS s'enregistrer comme nœud GPU (opt-in
// gpu_worker_enabled), mais ce sont deux tables/API différentes
// (GET /gpu-nodes, pas /api/agents). Tous les champs ci-dessous sont
// RÉELLEMENT mesurés (heartbeat agent, nvidia-smi pour gpuTempC) — jamais
// de score agrégé inventé.
export interface GpuNode {
  id: string; hostname: string; gpuName: string; gpuVendor: string
  gpuVramTotal: number; gpuVramFree: number; gpuUtil: number; tier: string
  status: string; ollamaUrl: string; vllmUrl?: string | null; pulledModels: string[]
  platform?: string | null; version: string; lastSeen?: string | null; lastBeat?: string | null
  tasksTotal: number; tasksDone: number; tasksFailed: number; totalTokens: number
  currentMissionId?: string | null; currentMissionAt?: string | null
  user?: { id: string; name?: string | null; email: string } | null
  live: boolean
  liveStats?: { gpuUtil: number; gpuTempC?: number; tokensPerSec?: number; gpuVramFree: number } | null
}

export interface GpuWorkerStats {
  connectedNodes: number
  pendingTasks:   number
  totals:         { dispatched: number; done: number; failed: number; tokens: number }
  nodes:          { nodeId: string; tier: string; gpuFree: number; gpuUtil: number; models: number; busy: boolean; uptimeSec: number; idleSec: number }[]
}

// Machine Identity Pipeline (Phase 1/2) — voir modules/agents/diagnostics/
// hardware-inventory.ts (source de vérité du calcul). null = jamais
// classifié (agent pré-Phase, ou pas encore reconnecté depuis la migration).
export type LlmProfileSlot = 'assistant' | 'reasoning' | 'code' | 'embeddings' | 'vision'
export type LlmProfile = Record<LlmProfileSlot, { role: string; model: string } | null>

// Faits bruts du dernier hardware_inventory — gpu vient de gpu_worker.py::
// detect_gpu() (cross-platform), cpuPct/memPct sont des UTILISATIONS
// instantanées (telemetry.py), PAS des specs statiques (nom CPU, RAM
// totale) : cette collecte-là (hardware_info(), Windows uniquement) n'est
// pas encore incluse dans l'event hardware_inventory — voir CLAUDE.md.
export interface HardwareInventorySnapshot {
  gpu?: { gpuName?: string; gpuVendor?: string; vramTotalGb?: number; vramFreeGb?: number; gpuUtil?: number; gpuTempC?: number } | null
  cpuPct?: number | null
  memPct?: number | null
}

// ─── DesktopAgent (canonique) ──────────────────────────────────────────────────

export interface DesktopAgent {
  id: string          // = device_id (clé primaire Prisma DesktopAgent)
  hostname: string
  platform: string
  status: 'ONLINE' | 'OFFLINE' | 'IDLE'
  lastSeen: string
  version?: string
  ollamaLocal?: string
  ollamaModels: string[]
  totalCommands: number
  createdAt: string
  capabilities?: string[]
  machineClass?: string | null
  nodeRole?: string | null
  schedulingWeight?: number | null
  llmProfile?: LlmProfile | null
  hardwareInventory?: HardwareInventorySnapshot | null
  machineClassifiedAt?: string | null
}

// ─── Command polling helper (résultat asynchrone via l'agent) ────────────────
// Même pattern partout (Paquets, PsTools, Network Toolkit, Inventaire) : pas
// besoin du relais temps réel de KVM/VNC pour une action ponctuelle, un
// polling simple sur GET /:id/commands/:cmdId suffit.

export interface AgentCommandResult {
  status: 'PENDING' | 'SENT' | 'DONE' | 'FAILED'
  result?: unknown
  error?: string | null
}

export async function pollCommandResult(agentId: string, commandId: string, timeoutMs = 20000): Promise<AgentCommandResult> {
  const start = Date.now()
  while (Date.now() - start < timeoutMs) {
    const res = await fetch(`${API}/api/agents/${agentId}/commands/${commandId}`, { credentials: 'include' })
    if (res.ok) {
      const cmd = await res.json() as AgentCommandResult
      if (cmd.status === 'DONE' || cmd.status === 'FAILED') return cmd
    }
    await new Promise(r => setTimeout(r, 1000))
  }
  return { status: 'FAILED', error: "Délai dépassé en attendant la réponse de l'agent" }
}

// ─── Temps ─────────────────────────────────────────────────────────────────────

export function relativeTime(iso: string) {
  const diff = Date.now() - new Date(iso).getTime()
  const m = Math.floor(diff / 60000)
  if (m < 1)  return "à l'instant"
  if (m < 60) return `il y a ${m} min`
  const h = Math.floor(m / 60)
  if (h < 24) return `il y a ${h}h`
  const d = Math.floor(h / 24)
  return `il y a ${d}j`
}

export function formatDuration(ms: number): string {
  const s = Math.round(ms / 1000)
  if (s < 60) return `${s}s`
  const m = Math.round(s / 60)
  if (m < 60) return `${m} min`
  const h = Math.round(m / 60)
  return `${h}h`
}

// Pour une échéance FUTURE (ex: ActionVerification.scheduledFor) — relativeTime
// ci-dessus suppose un timestamp passé et afficherait "à l'instant" pour une
// échéance qui n'est pas encore arrivée, ce qui inverserait le sens.
export function untilTime(iso: string) {
  const diff = new Date(iso).getTime() - Date.now()
  if (diff <= 0) return "d'un instant à l'autre"
  const m = Math.floor(diff / 60000)
  if (m < 60) return `dans ${Math.max(m, 1)} min`
  const h = Math.floor(m / 60)
  if (h < 24) return `dans ${h}h`
  const d = Math.floor(h / 24)
  return `dans ${d}j`
}

export function mosColor(mos: number | null): string {
  if (mos == null) return MUTED
  if (mos >= 4.0) return GREEN
  if (mos >= 3.0) return AMBER
  return RED
}
